
import _ from "lodash";
import Vue from "vue";
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";
import { mapActions, mapGetters, mapMutations } from "vuex";
import areAllPropertiesFalse from "~/assets/utils/areAllPropertiesFalse";
import highlightsDiff from "~/assets/utils/highlightsDiff";
import Modal from "~/components/Modal.vue";
import LargeSelect from "~/components/form/LargeSelect.vue";
import { AVSValue } from "~/entities/AVS";
import AVSAddressOptions from "~/entities/AVSAddressOptions";
import Notice from "~/entities/Notice";
import { Country, State } from "~/entities/Response/FormData";
import SavedAddress from "~/entities/SavedAddress";
import Validation from "~/entities/Validation";
import ValidationAuth from "~/entities/ValidationModules/ValidationAuth";
import * as Sentry from "@sentry/vue";

export const baseAddress = function () {
  return {
    addressId: 0,
    displayName: "",
    companyName: "",
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    state: "",
    postalCode: "",
    postalCodeExt: "",
    country: {
      code: "US",
    },
    phone: "",
    isPrimary: false,
    userId: "",
    latitude: "",
    longitude: "",
    type: 0,
    passedAvs: false,
    avsCheckedTimes: 0,
  };
};

export default Vue.extend({
  components: { LargeSelect, Modal, VueTelInput },
  middleware: "auth",
  data() {
    return {
      title: "Bikeflights.com | Bicycle Shipping Service For Cyclists",
      toggled: 0,
      actionNotice: null,
      validation: new Validation(new ValidationAuth()),
      addresses: [] as SavedAddress[],
      allowedCountries: [] as any,
      allowedStates: {} as any,
      newAddress: {} as SavedAddress,
      validated: false,
      updatedDetails: {} as AVSAddressOptions,
      isAVSModalOpen: false,
      isChanged: false as boolean,
      avsInfo: {} as AVSValue,
      addressId: null as number | null,
      PhoneValidationRules: {
        inputOptions: {
          placeholder: "",
          required: true,
          maxlength: 20,
        },
        validCharactersOnly: true,
        mode: "international",
        dropdownOptions: {
          disabled: false,
          showDialCodeInList: true,
          showDialCodeInSelection: false,
          showFlags: true,
          showSearchBox: true,
          tabindex: 0,
          width: "390px",
        },
      },
      submitted: false,
      phoneValid: false,
    };
  },
  head() {
    return {
      title: this.title,
    };
  },
  computed: {
    countryStates() {
      return (this as any).newAddress.country
        ? (this as any).allowedStates[(this as any).newAddress.country.code]
        : [];
    },
    ...mapGetters({
      activeNotices: "errorsBag/activeNotices" as any,
      alertResponses: "errorsBag/alertResponses" as any,

      // formData
      countries: "formData/allowedCountries",
      states: "formData/allowedStates",
    }),
  },
  async mounted() {
    this.validation.module.component = this;
    await this.fetchAllowedCountries();
    await this.fetchAllowedStates();

    this.allowedCountries = this.countries
      ?.map((el: Country) => {
        return {
          code: el.code as string,
          name: el.name as string,
          active: el.active as boolean,
          id: el.code as string,
          label: el.name as string,
        };
      })
      .filter((value) => value.active);

    this.mapStates(this.states);
    await this.loadAddress();
  },
  methods: {
    ...mapMutations({
      setLoading: "setLoading",
    }),
    ...mapActions({
      requestAlert: "errorsBag/requestAlert",
      clearActiveNotices: "errorsBag/clearActiveNotices",
      clearAlertResponses: "errorsBag/clearAlertResponses",
      removeNotice: "errorsBag/removeNotice",
      addNotice: "errorsBag/addNotice",
      // formData
      fetchAllowedCountries: "formData/fetchAllowedCountries",
      fetchAllowedStates: "formData/fetchAllowedStates",
      requestUserAddresses: "user/requestUserAddresses",
    }),
    /**
     * Fetches the user's addresses and updates the component's state.
     *
     * This method requests the user's addresses from the server and updates the
     * `addresses` data property with the fetched addresses. It also sets the
     * `rendered` property to true to indicate that the addresses have been loaded.
     *
     * @async
     * @function loadAddress
     * @returns {Promise<void>} - A promise that resolves when the addresses have been loaded.
     */
    async loadAddress() {
      // Fetch the user's addresses from the server
      const getAddress = await this.requestUserAddresses(true);
      const addresses = getAddress
        .map((el: any) => {
          return { ...el };
        })
        .sort((a, b) => {
          // Sort the addresses by their sortOrder property
          return a.sortOrder < b.sortOrder
            ? -1
            : a.sortOrder > b.sortOrder
            ? 1
            : 0;
        });
      // Indicate that the addresses have been rendered
      (this as any).rendered = true;

      // Update the addresses data property with the fetched addresses
      this.$nextTick(() => {
        Vue.set(this, "addresses", addresses);
      });
    },
    validateRequired() {
      this.validated = true;
      const requiredFields = {
        firstName: (this as any).newAddress.firstName,
        lastName: (this as any).newAddress.lastName,
        address1: (this as any).newAddress.address1,
        city: (this as any).newAddress.city,
        state: this.countryStates?.length ? (this as any).newAddress.state : 1, // if there is states available, let selecting pass trough, if not, then pass 1
        postalCode: (this as any).newAddress.postalCode,
        phone: (this as any).newAddress.phone,
      };
      return !Object.values(requiredFields).filter((value) => value === "")
        .length;
    },
    validatePhone(eventData: any) {
      // Validate the input element within the vue-tel-input component
      this.phoneValid = eventData.valid;
      return eventData.valid;
    },
    validateCompany() {
      const _this = this;
      if (
        _this.newAddress.companyName !== null &&
        typeof _this.newAddress.companyName !== "undefined" &&
        _this.newAddress.companyName !== ""
      ) {
        if (_this.newAddress.companyName.length > 34) {
          _this.validation.module.requestAlert({
            name: "companyName",
            type: "address",
          });
        } else {
          _this.removeNotice("AlertCompanyNameTooLong");
        }
      } else {
        _this.removeNotice("AlertCompanyNameTooLong");
      }
    },

    detailUpdated(updatedDetail: AVSAddressOptions, stageDetail: SavedAddress) {
      return (
        (updatedDetail.company &&
          stageDetail.companyName &&
          updatedDetail.company.trim() !== stageDetail.companyName.trim()) ||
        (updatedDetail.address1 &&
          stageDetail.address1 &&
          updatedDetail.address1.trim() !== stageDetail.address1.trim()) ||
        (updatedDetail.address2 &&
          stageDetail.address2 &&
          updatedDetail.address2.trim() !== stageDetail.address2.trim()) ||
        (updatedDetail.city &&
          stageDetail.city &&
          updatedDetail.city.trim() !== stageDetail.city.trim()) ||
        (updatedDetail.state &&
          updatedDetail.state &&
          stageDetail.state &&
          stageDetail.state &&
          updatedDetail.state.trim() !== stageDetail.state.trim()) ||
        (updatedDetail.postalCode &&
          stageDetail.postalCode &&
          updatedDetail.postalCode.trim() !== stageDetail.postalCode.trim()) ||
        (updatedDetail.countryCode &&
          updatedDetail.countryCode &&
          stageDetail.country &&
          stageDetail.country.code &&
          updatedDetail.countryCode.trim() !== stageDetail.country.code.trim())
      );
    },
    parseToUppercase(item: any) {
      for (const key in item) {
        if (typeof item[key] === "string") item[key] = item[key].toUpperCase();
      }
      return item;
    },
    checkIfDetailUpdated(updatedDetailNoParsed, stageDetailNoParsed): boolean {
      const updatedDetail: any = this.parseToUppercase(
        _.cloneDeep(updatedDetailNoParsed)
      );
      const stageDetail: any = this.parseToUppercase(
        _.cloneDeep(stageDetailNoParsed)
      );
      const isDifferent = (a, b) => {
        return a !== undefined && b !== undefined && a.trim() !== b.trim();
      };
      return (
        isDifferent(updatedDetail?.company, stageDetail?.companyName) ||
        isDifferent(updatedDetail?.address1, stageDetail?.address1) ||
        isDifferent(updatedDetail?.address2, stageDetail?.address2) ||
        isDifferent(updatedDetail?.city, stageDetail?.city) ||
        isDifferent(updatedDetail?.state, stageDetail?.state) ||
        isDifferent(updatedDetail?.postalCode, stageDetail?.postalCode) ||
        isDifferent(updatedDetail?.country?.code, stageDetail?.country?.code) ||
        false
      );
    },

    hideModal() {
      this.isAVSModalOpen = false;
    },

    async confirmActionModal() {
      try {
        const keys = Object.keys(this.updatedDetails);
        for (let i = 0; i < keys.length; i++) {
          const keyName = keys[i];
          if (keyName in this.newAddress) {
            this.newAddress[keyName] = this.updatedDetails[keyName];
          }
        }
        this.isAVSModalOpen = false;
        const response = await this.createAddressRequest(
          this.avsInfo,
          this.addressId
        );
        if (response) {
          await this.loadAddress();
          (this as any).toggled = 0;
          this.$nextTick(() => {
            this.newAddress = new SavedAddress();
            this.newAddress.country = null;
            (this as any).actionNotice = new Notice({
              expletive: "Success",
              message: "Address successfully added.",
              type: "alert-success",
              dismissable: true,
              active: true,
            });
          });
          return response;
        }
      } catch (err) {
        Sentry.captureException(err);
        return false;
      }
    },

    async createAddressRequest(data: AVSValue, addressId?: number | null) {
      try {
        if (!addressId) {
          const addressRequest = await this.$axios.$post(
            `${(this as any).$config.apiURL}/api/Address`,
            {
              ...(this as any).newAddress,
              country: ((this as any).newAddress.country as any).id,
              passedAvs: data.passedAvs,

              /* @TODO: we can discard these as they are not used in the backend per Travis */
              PostalCodeExt: "ext",
              latitude: "0",
              longitude: "0",
            }
          );
          return addressRequest;
        } else {
          const addressRequest = await this.$axios.$put(
            `${(this as any).$config.apiURL}/api/Address/${addressId}`,
            {
              ...(this as any).newAddress,

              country: ((this as any).newAddress.country as any).id,
              passedAvs: data.passedAvs,

              /* @TODO: we can discard these as they are not used in the backend per Travis */
              PostalCodeExt: "ext",
              latitude: "0",
              longitude: "0",
            }
          );
          return addressRequest;
        }
      } catch (err) {
        return err;
      }
    },

    mapStates(states: Array<State>) {
      try {
        const _this = this;
        for (let step = 0; step < states.length; step++) {
          const country = states[step].country;
          const countryStates = _this.allowedStates[country];
          if (
            typeof countryStates === "undefined" ||
            (countryStates && countryStates.length === 0)
          ) {
            _this.allowedStates[country] = [];
          }
          const state = {} as State;
          Object.assign(state, states[step]);
          state.id = state.stateprovcode;
          state.label = state.stateprov;
          _this.allowedStates[country].push(state);
        }
        return true;
      } catch (e) {
        return false;
      }
    },

    selectCountry(selection: any) {
      if (selection.option.code !== this.newAddress.country?.code) {
        this.newAddress.state = "";
      }
      this.newAddress.country = selection.option;
    },

    selectState(selection: any) {
      this.newAddress.state = selection.option.stateprovcode;
    },

    checkState() {
      if (this.newAddress.state) {
        return this.countryStates.find(
          (state) =>
            state.label === this.newAddress.state ||
            state.id === this.newAddress.state
        );
      }
      return null;
    },

    showAdd() {
      // prefill
      (this as any).newAddress = baseAddress();
      if (this.allowedCountries.length)
        (this as any).newAddress.country = this.allowedCountries.find(
          (country) => country.code === "US"
        );

      this.actionNotice = null;
      this.validated = false;
      this.toggled = 1;

      window.scrollTo(0, 0);
    },

    async showEdit(address) {
      // prefill
      this.newAddress = { ...address };
      this.newAddress.country = await this.allowedCountries.find(
        (country) =>
          country.code === (this as any).newAddress.country ||
          country.label === (this as any).newAddress.country
      );

      this.actionNotice = null;
      this.validated = false;
      this.toggled = 2;
      window.scrollTo(0, 0);
    },

    async deleteAddress(addressId) {
      this.actionNotice = null;
      try {
        const response = await this.$axios
          .$delete(`${(this as any).$config.apiURL}/api/Address/${addressId}`)
          .catch(function (error) {
            Sentry.captureException(error);
            return false;
          });
        if (response) {
          (this as any).actionNotice = new Notice({
            expletive: "Success",
            message: "Address successfully deleted.",
            type: "alert-success",
            dismissable: true,
            active: true,
          });
          await this.loadAddress();
        }
        return false;
      } catch (e) {
        Sentry.captureException(e);
        return false;
      }
    },

    async addNewAddress() {
      await this.saveAddress();
    },

    async editAddress(addressId: number) {
      await this.saveAddress(addressId);
    },

    async saveAddress(addressId?: number) {
      this.submitted = true;
      if (!this.validateRequired() || !this.phoneValid) return;

      this.actionNotice = null;

      try {
        const response = await this.$axios
          .$get(`/api/Avs`, {
            params: {
              company: this.newAddress.companyName,
              address1: this.newAddress.address1,
              address2: this.newAddress.address2,
              city: this.newAddress.city,
              state: this.newAddress.state,
              postalCode: this.newAddress.postalCode,
              country: this.newAddress.country?.code,
            },
          })
          .then(async (data: AVSValue) => {
            if (data.success && data.passedAvs) {
              this.isChanged = data.isChanged;
              if (data.validAddress) {
                this.updatedDetails = data.validAddress;
              }
              if (
                data.changedFlags &&
                !areAllPropertiesFalse(data.changedFlags)
              ) {
                this.avsInfo = data;
                this.addressId = addressId || null;
                this.isAVSModalOpen = true;
              } else {
                this.isAVSModalOpen = false;
                const keys = Object.keys(this.updatedDetails);
                for (let i = 0; i < keys.length; i++) {
                  const keyName = keys[i];
                  if (keyName in this.newAddress) {
                    this.newAddress[keyName] = this.updatedDetails[keyName];
                  }
                }
                const response = await this.createAddressRequest(
                  data,
                  addressId
                );
                return response;
              }
            } else
              (this as any).actionNotice = new Notice({
                expletive: "Oh Snap",
                message:
                  data.errorMessage?.trim() ||
                  "There was an error validating the address, please try again",
                type: "alert-danger",
                dismissable: true,
                active: true,
              });
          })
          .then((response) => response)
          .catch((err) => {
            (this as any).actionNotice = new Notice({
              expletive: "Oh Snap",
              message: `There was an error ${
                addressId ? "updating" : "adding"
              } the address, please try again`,
              type: "alert-danger",
              dismissable: true,
              active: true,
            });
          });

        if (response) {
          this.toggled = 0;
          this.$nextTick(() => {
            this.newAddress = new SavedAddress();
            this.newAddress.country = null;
            (this as any).actionNotice = new Notice({
              expletive: "Success",
              message: `Address successfully ${
                addressId ? "updated" : "added"
              }.`,
              type: "alert-success",
              dismissable: true,
              active: true,
            });
          });
          await this.loadAddress();
        }
        return false;
      } catch (e) {
        Sentry.captureException(e);
        return false;
      }
    },
    highlightsDiff,
  },
  fetchOnServer: false,
});
